/*
 * Brunch map (sections/brunch-map.liquid)
 *
 * MapLibre GL (v5, assets/maplibre-gl.js) on OpenFreeMap vector tiles (free, commercial use allowed, no key),
 * restyled in the Maison Giulia palette. Venues come from the HTML cards rendered
 * by the section, so the guide stays readable without JavaScript. Filters, cards,
 * popup and pagination are plain DOM; the map only draws pins and clusters.
 */
(function () {
  'use strict';

  var FRANCE = [[-5.2, 41.3], [9.7, 51.1]];
  var HOME = { name: 'Grasse', lng: 6.9237, lat: 43.6589 };
  var PIN = '#ff4c25';
  var PIN_DARK = '#d63a17';
  var INK = '#111111';
  var WINE = '#5a1e1b';
  var GOLD = '#7e6b45';
  var PAGE_SIZE = 9;
  var DESKTOP = '(min-width: 990px)';
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function fold(str) {
    return (str || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  }

  function budgetOf(price) {
    if (!isFinite(price) || price <= 0) return 0;
    if (price < 80) return 1;
    if (price <= 130) return 2;
    return 3;
  }

  function each(list, fn) {
    Array.prototype.forEach.call(list, fn);
  }

  // Recolour the OpenFreeMap "positron" style in the brand palette.
  function brandStyle(style, lang) {
    var nameField = ['coalesce', ['get', 'name:' + lang], ['get', 'name']];
    style.layers.forEach(function (layer) {
      var id = layer.id;
      var p = layer.paint || (layer.paint = {});
      if (layer.type === 'background') p['background-color'] = '#fbf7f0';
      else if (id === 'water') p['fill-color'] = '#e3ddd2';
      else if (id === 'park' || id.indexOf('landcover') === 0) p['fill-color'] = '#eeeee0';
      else if (id === 'landuse_residential') p['fill-color'] = '#f6f0e6';
      else if (id === 'building') { p['fill-color'] = '#efe6d8'; p['fill-outline-color'] = '#e6dccb'; }
      else if (id === 'waterway') p['line-color'] = '#e3ddd2';
      else if (id.indexOf('boundary') === 0) p['line-color'] = 'rgba(90, 30, 27, 0.35)';
      else if (layer.type === 'line' && id.indexOf('casing') !== -1) p['line-color'] = '#e8dece';
      else if (layer.type === 'line' && (id.indexOf('highway') === 0 || id.indexOf('road') === 0 || id.indexOf('tunnel') === 0)) p['line-color'] = '#ffffff';
      if (layer.type === 'symbol') {
        p['text-color'] = id.indexOf('label_city') === 0 || id.indexOf('label_country') === 0 ? WINE : '#7d7366';
        p['text-halo-color'] = '#fbf7f0';
        p['text-halo-width'] = 1.4;
        if (layer.layout && layer.layout['text-field'] && id.indexOf('shield') === -1) {
          layer.layout['text-field'] = nameField;
        }
      }
    });
    return style;
  }

  // Teardrop pin drawn on a canvas so it can be used as a map icon.
  function pinImage(fill, dot) {
    var r = 2;
    var w = 26 * r;
    var h = 34 * r;
    var c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    var g = c.getContext('2d');
    g.scale(r, r);
    g.beginPath();
    g.moveTo(13, 33);
    g.bezierCurveTo(11, 27, 3, 22, 3, 14);
    g.arc(13, 14, 10, Math.PI, 0);
    g.bezierCurveTo(23, 22, 15, 27, 13, 33);
    g.closePath();
    g.fillStyle = fill;
    g.fill();
    g.lineWidth = 1.6;
    g.strokeStyle = '#ffffff';
    g.stroke();
    g.beginPath();
    g.arc(13, 14, 3.8, 0, Math.PI * 2);
    g.fillStyle = dot;
    g.fill();
    return { width: w, height: h, data: g.getImageData(0, 0, w, h).data };
  }

  function BrunchMap(root) {
    this.root = root;
    this.stage = root.querySelector('[data-stage]');
    this.canvas = root.querySelector('[data-canvas]');
    this.filtersEl = root.querySelector('[data-filters]');
    this.panel = root.querySelector('[data-filters-panel]');
    this.popup = root.querySelector('[data-popup]');
    this.popupBody = root.querySelector('[data-popup-body]');
    this.emptyEl = root.querySelector('[data-empty]');
    this.moreBtn = root.querySelector('[data-more]');
    this.isDesktop = window.matchMedia(DESKTOP);
    this.lang = root.getAttribute('data-lang') || 'en';

    this.state = { q: '', region: 'all', type: '', tags: [], budgets: [] };
    this.limit = PAGE_SIZE;
    this.selected = null;
    this.map = null;

    this.venues = Array.prototype.map.call(root.querySelectorAll('[data-venue]'), function (card) {
      return {
        id: card.getAttribute('data-id'),
        card: card,
        name: card.querySelector('.brunch-card__name').textContent.trim(),
        search: fold(card.getAttribute('data-keywords')),
        region: card.getAttribute('data-region'),
        type: card.getAttribute('data-type'),
        tags: (card.getAttribute('data-tags') || '').split(' ').filter(Boolean),
        giulia: card.getAttribute('data-giulia') === 'true',
        budget: budgetOf(parseFloat(card.getAttribute('data-price'))),
        lat: parseFloat(card.getAttribute('data-lat')),
        lng: parseFloat(card.getAttribute('data-lng')),
        visible: true
      };
    }).filter(function (v) {
      return isFinite(v.lat) && isFinite(v.lng);
    });
    this.byId = {};
    var self = this;
    this.venues.forEach(function (v) { self.byId[v.id] = v; });

    // Cards with a photo lead the grid; the rest keep their order.
    var list = root.querySelector('[data-list]');
    var hasPhoto = function (v) { return !!v.card.querySelector('.brunch-card__media img'); };
    this.venues = this.venues.filter(hasPhoto).concat(this.venues.filter(function (v) { return !hasPhoto(v); }));
    this.venues.forEach(function (v) { list.appendChild(v.card); });

    // City pages open on their own venues instead of the whole of France.
    this.home = FRANCE;
    if (root.hasAttribute('data-local') && this.venues.length) {
      var lngs = this.venues.map(function (v) { return v.lng; });
      var lats = this.venues.map(function (v) { return v.lat; });
      this.home = [
        [Math.min.apply(null, lngs), Math.min.apply(null, lats)],
        [Math.max.apply(null, lngs), Math.max.apply(null, lats)]
      ];
    }

    this.setFiltersOpen(this.isDesktop.matches, true);
    this.bind();
    this.applyFilters(false);
    this.lazyInitMap();

    if (this.isDesktop.addEventListener) {
      this.isDesktop.addEventListener('change', function () {
        self.setFiltersOpen(self.isDesktop.matches, true);
      });
    }
  }

  /* ---------- Map ---------- */

  // MapLibre and the tiles are only loaded once the map is about to scroll into view.
  BrunchMap.prototype.lazyInitMap = function () {
    var self = this;
    var start = function () {
      if (self.mapStarted) return;
      self.mapStarted = true;
      if (!window.maplibregl && !document.querySelector('script[data-maplibre]')) {
        var script = document.createElement('script');
        script.src = self.root.getAttribute('data-lib-url');
        script.async = true;
        script.setAttribute('data-maplibre', '');
        document.head.appendChild(script);
      }
      self.waitForLibrary(0);
    };
    if (!('IntersectionObserver' in window)) return start();
    var io = new IntersectionObserver(function (entries) {
      if (entries.some(function (e) { return e.isIntersecting; })) {
        io.disconnect();
        start();
      }
    }, { rootMargin: '300px' });
    io.observe(this.stage);
  };

  BrunchMap.prototype.waitForLibrary = function (tries) {
    var self = this;
    if (window.maplibregl) return this.initMap();
    if (tries > 150) return; // library failed to load; the cards still work
    setTimeout(function () { self.waitForLibrary(tries + 1); }, 100);
  };

  BrunchMap.prototype.initMap = function () {
    var self = this;
    var fr = this.lang === 'fr';
    fetch(this.root.getAttribute('data-style-url'))
      .then(function (res) { return res.json(); })
      .then(function (style) {
        self.map = new maplibregl.Map({
          container: self.canvas,
          style: brandStyle(style, self.lang),
          bounds: self.home,
          fitBoundsOptions: { padding: self.padding(), maxZoom: 14 },
          minZoom: 3,
          maxZoom: 18,
          attributionControl: { compact: true },
          cooperativeGestures: true,
          dragRotate: false,
          pitchWithRotate: false,
          touchPitch: false,
          locale: fr
            ? {
                'CooperativeGesturesHandler.WindowsHelpText': 'Utilisez Ctrl + molette pour zoomer',
                'CooperativeGesturesHandler.MacHelpText': 'Utilisez ⌘ + molette pour zoomer',
                'CooperativeGesturesHandler.MobileHelpText': 'Utilisez deux doigts pour déplacer la carte'
              }
            : {}
        });
        self.map.touchZoomRotate.disableRotation();
        self.map.on('load', function () { self.onMapLoad(); });
      })
      .catch(function () {
        self.root.classList.add('is-map-error');
      });
  };

  BrunchMap.prototype.onMapLoad = function () {
    var self = this;
    var map = this.map;
    map.addImage('pin', pinImage(PIN, '#ffffff'), { pixelRatio: 2 });
    map.addImage('pin-giulia', pinImage(PIN, WINE), { pixelRatio: 2 });
    map.addImage('pin-hover', pinImage(PIN_DARK, '#ffffff'), { pixelRatio: 2 });
    map.addImage('pin-selected', pinImage(INK, '#ffffff'), { pixelRatio: 2 });

    map.addSource('venues', {
      type: 'geojson',
      data: this.geojson(),
      cluster: true,
      clusterRadius: 42,
      clusterMaxZoom: 15
    });

    map.addLayer({
      id: 'clusters-halo',
      type: 'circle',
      source: 'venues',
      filter: ['has', 'point_count'],
      paint: { 'circle-color': PIN, 'circle-opacity': 0.22, 'circle-radius': ['step', ['get', 'point_count'], 21, 10, 24] }
    });
    map.addLayer({
      id: 'clusters',
      type: 'circle',
      source: 'venues',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': PIN,
        'circle-radius': ['step', ['get', 'point_count'], 15, 10, 18],
        'circle-stroke-width': 1.6,
        'circle-stroke-color': '#ffffff'
      }
    });
    map.addLayer({
      id: 'cluster-count',
      type: 'symbol',
      source: 'venues',
      filter: ['has', 'point_count'],
      layout: { 'text-field': ['get', 'point_count_abbreviated'], 'text-font': ['Noto Sans Bold'], 'text-size': 12 },
      paint: { 'text-color': '#ffffff' }
    });
    map.addLayer({
      id: 'pins',
      type: 'symbol',
      source: 'venues',
      filter: ['!', ['has', 'point_count']],
      layout: {
        'icon-image': ['case', ['get', 'giulia'], 'pin-giulia', 'pin'],
        'icon-anchor': 'bottom',
        'icon-allow-overlap': true,
        'icon-ignore-placement': true
      }
    });
    map.addLayer({
      id: 'pins-hover',
      type: 'symbol',
      source: 'venues',
      filter: ['==', ['get', 'id'], ''],
      layout: { 'icon-image': 'pin-hover', 'icon-anchor': 'bottom', 'icon-size': 1.15, 'icon-allow-overlap': true }
    });
    map.addLayer({
      id: 'pins-selected',
      type: 'symbol',
      source: 'venues',
      filter: ['==', ['get', 'id'], ''],
      layout: { 'icon-image': 'pin-selected', 'icon-anchor': 'bottom', 'icon-size': 1.2, 'icon-allow-overlap': true }
    });

    // Grasse, home of Maison Giulia.
    map.addSource('home', {
      type: 'geojson',
      data: { type: 'Feature', geometry: { type: 'Point', coordinates: [HOME.lng, HOME.lat] }, properties: {} }
    });
    map.addLayer({
      id: 'home-dot',
      type: 'circle',
      source: 'home',
      paint: { 'circle-radius': 4, 'circle-color': GOLD, 'circle-stroke-width': 1.5, 'circle-stroke-color': '#ffffff' }
    }, 'clusters-halo');
    map.addLayer({
      id: 'home-label',
      type: 'symbol',
      source: 'home',
      layout: {
        'text-field': HOME.name + '\n' + this.root.getAttribute('data-home-label'),
        'text-font': ['Noto Sans Bold'],
        'text-size': 11,
        'text-anchor': 'left',
        'text-offset': [0.8, 0],
        'text-justify': 'left',
        'text-letter-spacing': 0.06
      },
      paint: { 'text-color': GOLD, 'text-halo-color': '#fbf7f0', 'text-halo-width': 1.6 }
    }, 'clusters-halo');

    map.on('click', 'clusters', function (e) {
      var f = e.features[0];
      self.closePopup();
      map.getSource('venues').getClusterExpansionZoom(f.properties.cluster_id).then(function (zoom) {
        map.easeTo({ center: f.geometry.coordinates, zoom: zoom + 0.5, duration: reduceMotion ? 0 : 500 });
      });
    });
    map.on('click', 'pins', function (e) {
      var v = self.byId[e.features[0].properties.id];
      if (v) self.select(v, false);
    });
    ['clusters', 'pins'].forEach(function (layer) {
      map.on('mouseenter', layer, function () { map.getCanvas().style.cursor = 'pointer'; });
      map.on('mouseleave', layer, function () { map.getCanvas().style.cursor = ''; });
    });

    this.root.classList.add('is-loaded');
    this.refreshMap(false);
  };

  BrunchMap.prototype.geojson = function () {
    return {
      type: 'FeatureCollection',
      features: this.venues
        .filter(function (v) { return v.visible; })
        .map(function (v) {
          return {
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [v.lng, v.lat] },
            properties: { id: v.id, giulia: v.giulia }
          };
        })
    };
  };

  // Space taken by the open filter panel (desktop) so fits land in the free area.
  BrunchMap.prototype.padding = function () {
    var left = this.isDesktop.matches && this.filtersOpen ? this.panel.offsetWidth + 48 : 32;
    return { top: 48, bottom: 48, left: left, right: 64 };
  };

  BrunchMap.prototype.fitVenues = function (venues) {
    if (!this.map) return;
    var duration = reduceMotion ? 0 : 700;
    if (!venues || !venues.length) {
      this.map.fitBounds(this.home, { padding: this.padding(), maxZoom: 14, duration: duration });
      return;
    }
    var b = new maplibregl.LngLatBounds();
    venues.forEach(function (v) { b.extend([v.lng, v.lat]); });
    this.map.fitBounds(b, { padding: this.padding(), maxZoom: 15, duration: duration });
  };

  BrunchMap.prototype.refreshMap = function (fit) {
    if (!this.map || !this.map.getSource('venues')) return;
    this.map.getSource('venues').setData(this.geojson());
    this.map.setFilter('pins-selected', ['==', ['get', 'id'], this.selected ? this.selected.id : '']);
    if (fit) this.fitVenues(this.activeCount() === 0 ? null : this.shown);
  };

  // Hovering a card lights up its pin.
  BrunchMap.prototype.highlight = function (v, on) {
    if (!this.map || !this.map.getLayer('pins-hover')) return;
    this.map.setFilter('pins-hover', ['==', ['get', 'id'], on ? v.id : '']);
  };

  /* ---------- Filters ---------- */

  BrunchMap.prototype.matches = function (v, s) {
    if (s.region !== 'all' && v.region !== s.region) return false;
    if (s.type && v.type !== s.type) return false;
    if (s.budgets.length && s.budgets.indexOf(v.budget) === -1) return false;
    for (var i = 0; i < s.tags.length; i++) {
      if (v.tags.indexOf(s.tags[i]) === -1) return false;
    }
    if (s.q && v.search.indexOf(s.q) === -1) return false;
    return true;
  };

  BrunchMap.prototype.countWith = function (patch) {
    var s = {};
    for (var k in this.state) s[k] = this.state[k];
    for (var p in patch) s[p] = patch[p];
    var self = this;
    return this.venues.filter(function (v) { return self.matches(v, s); }).length;
  };

  BrunchMap.prototype.activeCount = function () {
    var s = this.state;
    return (s.q ? 1 : 0) + (s.type ? 1 : 0) + (s.region !== 'all' ? 1 : 0) + s.tags.length + s.budgets.length;
  };

  // Faceted counts: what each option would give with everything else unchanged.
  BrunchMap.prototype.updateFacets = function () {
    var self = this;
    var s = this.state;

    each(this.root.querySelectorAll('[data-regions] [data-region]'), function (chip) {
      var key = chip.getAttribute('data-region');
      var on = s.region === key;
      chip.classList.toggle('is-active', on);
      chip.setAttribute('aria-pressed', on);
      var n = chip.querySelector('[data-n]');
      if (n) {
        var count = self.countWith({ region: key });
        n.textContent = count;
        chip.disabled = !on && count === 0;
      }
    });

    each(this.root.querySelectorAll('[data-type-filter]'), function (btn) {
      var key = btn.getAttribute('data-type-filter');
      var on = s.type === key;
      var n = self.countWith({ type: key });
      btn.classList.toggle('is-active', on);
      btn.setAttribute('aria-pressed', on);
      btn.disabled = !on && n === 0;
      btn.querySelector('[data-n]').textContent = n;
    });

    each(this.root.querySelectorAll('[data-tag]'), function (btn) {
      var key = btn.getAttribute('data-tag');
      var on = s.tags.indexOf(key) !== -1;
      var n = on ? self.countWith({}) : self.countWith({ tags: s.tags.concat(key) });
      btn.classList.toggle('is-active', on);
      btn.setAttribute('aria-pressed', on);
      btn.disabled = !on && n === 0;
      btn.querySelector('[data-n]').textContent = n;
    });

    each(this.root.querySelectorAll('[data-budget]'), function (btn) {
      var key = parseInt(btn.getAttribute('data-budget'), 10);
      var on = s.budgets.indexOf(key) !== -1;
      var n = self.countWith({ budgets: [key] });
      btn.classList.toggle('is-active', on);
      btn.setAttribute('aria-pressed', on);
      btn.disabled = !on && n === 0;
    });

    var active = this.activeCount();
    var badge = this.root.querySelector('[data-active-count]');
    badge.hidden = active === 0;
    badge.textContent = active;
    each(this.root.querySelectorAll('.bm-filters__clear'), function (b) {
      b.hidden = active === 0;
    });
  };

  BrunchMap.prototype.applyFilters = function (fit) {
    var self = this;
    var shown = [];
    this.venues.forEach(function (v) {
      v.visible = self.matches(v, self.state);
      if (v.visible) shown.push(v);
    });
    this.shown = shown;

    if (this.selected && !this.selected.visible) this.closePopup(true);

    var n = shown.length;
    var label = n + ' ' + this.root.getAttribute(n === 1 ? 'data-count-one' : 'data-count-other');
    each(this.root.querySelectorAll('[data-count]'), function (c) {
      c.textContent = label;
    });
    each(this.root.querySelectorAll('[data-count-n]'), function (c) {
      c.textContent = n;
    });
    this.emptyEl.hidden = n !== 0;
    this.updateFacets();
    this.paginate();
    this.refreshMap(fit && n > 0);
  };

  BrunchMap.prototype.paginate = function () {
    var self = this;
    var shownIndex = 0;
    this.venues.forEach(function (v) {
      if (!v.visible) {
        v.card.hidden = true;
        return;
      }
      v.card.hidden = shownIndex >= self.limit;
      shownIndex++;
    });
    this.moreBtn.hidden = this.shown.length <= this.limit;
  };

  BrunchMap.prototype.setFiltersOpen = function (open, silent) {
    this.filtersOpen = open;
    this.root.classList.toggle('is-filters-open', open);
    this.root.querySelector('[data-filters-toggle]').setAttribute('aria-expanded', open);
    if (open && !this.isDesktop.matches) this.closePopup(true);
    if (!silent && this.map) this.map.resize();
  };

  /* ---------- Selection ---------- */

  BrunchMap.prototype.select = function (v, fly) {
    this.selected = v;
    this.venues.forEach(function (o) {
      o.card.classList.toggle('is-selected', o === v);
    });
    if (!this.isDesktop.matches && this.filtersOpen) this.setFiltersOpen(false, true);

    var media = v.card.querySelector('.brunch-card__media').cloneNode(true);
    var body = v.card.querySelector('.brunch-card__body').cloneNode(true);
    var locate = body.querySelector('[data-locate]');
    if (locate) locate.remove();
    var blurb = body.querySelector('.brunch-card__blurb');
    if (blurb && !this.isDesktop.matches) blurb.remove();
    this.popupBody.textContent = '';
    this.popupBody.appendChild(media);
    this.popupBody.appendChild(body);
    this.popup.hidden = false;

    if (!this.map) return;
    this.map.setFilter('pins-selected', ['==', ['get', 'id'], v.id]);

    // Put the pin in the free part of the map, above the popup.
    var rect = this.stage.getBoundingClientRect();
    var pad = this.padding();
    var desktop = this.isDesktop.matches;
    var popupW = desktop ? this.popup.offsetWidth + 24 : 0;
    var targetX = pad.left + (rect.width - pad.left - popupW) / 2;
    var targetY = desktop ? rect.height / 2 : rect.height * 0.28;
    this.map.easeTo({
      center: [v.lng, v.lat],
      zoom: fly ? Math.max(this.map.getZoom(), 14) : this.map.getZoom(),
      offset: [targetX - rect.width / 2, targetY - rect.height / 2],
      duration: reduceMotion ? 0 : 600
    });
  };

  BrunchMap.prototype.closePopup = function () {
    if (!this.selected && this.popup.hidden) return;
    this.selected = null;
    this.popup.hidden = true;
    this.venues.forEach(function (o) {
      o.card.classList.remove('is-selected');
    });
    if (this.map && this.map.getLayer('pins-selected')) {
      this.map.setFilter('pins-selected', ['==', ['get', 'id'], '']);
    }
  };

  /* ---------- Events ---------- */

  BrunchMap.prototype.bind = function () {
    var self = this;
    var root = this.root;

    root.querySelector('[data-filters-toggle]').addEventListener('click', function () {
      self.setFiltersOpen(!self.filtersOpen);
    });
    each(root.querySelectorAll('[data-filters-close]'), function (btn) {
      btn.addEventListener('click', function () {
        self.setFiltersOpen(false);
      });
    });

    var search = root.querySelector('[data-search-input]');
    var searchTimer;
    search.addEventListener('input', function () {
      clearTimeout(searchTimer);
      searchTimer = setTimeout(function () {
        self.state.q = fold(search.value.trim());
        self.limit = PAGE_SIZE;
        self.applyFilters(true);
      }, 180);
    });

    each(root.querySelectorAll('[data-regions] [data-region]'), function (chip) {
      chip.addEventListener('click', function () {
        var key = chip.getAttribute('data-region');
        self.state.region = self.state.region === key && key !== 'all' ? 'all' : key;
        self.limit = PAGE_SIZE;
        self.applyFilters(true);
      });
    });

    each(root.querySelectorAll('[data-type-filter]'), function (btn) {
      btn.addEventListener('click', function () {
        var key = btn.getAttribute('data-type-filter');
        self.state.type = self.state.type === key ? '' : key;
        self.limit = PAGE_SIZE;
        self.applyFilters(true);
      });
    });

    each(root.querySelectorAll('[data-tag]'), function (btn) {
      btn.addEventListener('click', function () {
        var key = btn.getAttribute('data-tag');
        var i = self.state.tags.indexOf(key);
        if (i === -1) self.state.tags.push(key);
        else self.state.tags.splice(i, 1);
        self.limit = PAGE_SIZE;
        self.applyFilters(true);
      });
    });

    each(root.querySelectorAll('[data-budget]'), function (btn) {
      btn.addEventListener('click', function () {
        var key = parseInt(btn.getAttribute('data-budget'), 10);
        var i = self.state.budgets.indexOf(key);
        if (i === -1) self.state.budgets.push(key);
        else self.state.budgets.splice(i, 1);
        self.limit = PAGE_SIZE;
        self.applyFilters(true);
      });
    });

    each(root.querySelectorAll('[data-clear]'), function (btn) {
      btn.addEventListener('click', function () {
        self.state = { q: '', region: 'all', type: '', tags: [], budgets: [] };
        search.value = '';
        self.limit = PAGE_SIZE;
        self.closePopup();
        self.applyFilters(true);
      });
    });

    this.moreBtn.addEventListener('click', function () {
      self.limit += PAGE_SIZE;
      self.paginate();
    });

    this.venues.forEach(function (v) {
      v.card.addEventListener('mouseenter', function () { self.highlight(v, true); });
      v.card.addEventListener('mouseleave', function () { self.highlight(v, false); });
      var btn = v.card.querySelector('[data-locate]');
      if (!btn) return;
      btn.addEventListener('click', function () {
        self.highlight(v, false);
        self.stage.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
        self.select(v, true);
      });
    });

    each(root.querySelectorAll('[data-zoom]'), function (btn) {
      btn.addEventListener('click', function () {
        if (!self.map) return;
        var kind = btn.getAttribute('data-zoom');
        if (kind === 'reset') {
          self.closePopup();
          return self.fitVenues(self.activeCount() === 0 ? null : self.shown);
        }
        if (kind === 'in') self.map.zoomIn();
        else self.map.zoomOut();
      });
    });

    root.querySelector('[data-popup-close]').addEventListener('click', function () {
      self.closePopup();
    });
    root.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (self.selected) self.closePopup();
      else if (self.filtersOpen && !self.isDesktop.matches) self.setFiltersOpen(false);
    });
  };

  function init(scope) {
    each((scope || document).querySelectorAll('[data-section-type="brunch-map"]'), function (root) {
      if (root.brunchMap) return;
      root.brunchMap = new BrunchMap(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // Theme editor: re-initialise when the section is re-rendered.
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
})();
